/**
 * Offline-login credentials config on TotalAccessOfflineDB (replaces legacy TotalAccessOfflineAuthDB).
 */
import Dexie from 'dexie';

import offlinePayloadCrypto from './offlineCrypto';
import type { OfflineDomainRecord } from './offlineDb';
import offlineDb from './offlineDb';

const DOMAIN = 'offline-auth' as const;

const STORE_CREDENTIALS = 'credentials' as const;
const STORE_CONFIG = 'credential_config' as const;

/** Global singleton config rows use client_id = 0. */
export const AUTH_CONFIG_CLIENT_ID = 0;
export const AUTH_CONFIG_ENTITY_ID = 'config';

function getDb() {
  return offlineDb;
}

function toEntityId(value: string | number | undefined | null): string {
  return String(value ?? '');
}

async function decryptRecordPayload<T>(record: OfflineDomainRecord | undefined): Promise<T | undefined> {
  if (!record) {
    return undefined;
  }
  return offlinePayloadCrypto.decryptValue<T>(record.payload);
}

async function buildRecordRow<T extends Record<string, unknown>>(
  store: typeof STORE_CREDENTIALS | typeof STORE_CONFIG,
  clientId: number,
  entityId: string,
  item: T,
  existing: OfflineDomainRecord | undefined,
): Promise<OfflineDomainRecord> {
  const now = new Date().toISOString();
  const payload = await offlinePayloadCrypto.encryptValue(item);

  return {
    id: existing?.id,
    domain: DOMAIN,
    store,
    client_id: clientId,
    entity_id: entityId,
    sync_status: 'synced',
    created_at: existing?.created_at || now,
    updated_at: now,
    payload,
    payload_size: payload.length,
  };
}

async function getStoreRow(
  store: typeof STORE_CREDENTIALS | typeof STORE_CONFIG,
  clientId: number,
  entityId: string,
): Promise<OfflineDomainRecord | undefined> {
  const db = getDb();
  if (!db) {
    return undefined;
  }

  return db.records
    .where('[domain+store+client_id+entity_id]')
    .equals([DOMAIN, store, clientId, entityId])
    .first();
}

let legacyMigratePromise: Promise<void> | undefined;

export interface ImportedOfflineCredential {
  id: string;
  email: string;
  passwordHash: string;
  salt: string;
  client_id: number;
  user_id: number;
  userData: string;
  tokens: string;
  pinHash?: string;
  pinSalt?: string;
  created_at: string | Date;
  last_used_at: string | Date;
  expires_at: string | Date;
  sync_required: boolean;
  device_fingerprint: string;
  login_attempts: number;
  locked_until?: string | Date | null;
}

export interface ImportedOfflineAuthConfig {
  id: 'config';
  max_offline_days: number;
  max_login_attempts: number;
  lockout_minutes: number;
  require_biometric: boolean;
  allow_pin_unlock: boolean;
  last_online_sync: Date | string | null;
}

const authOfflineStore = {
  async upsertCredentialPayload(
    credentialClientId: number,
    credentialStorageId: string,
    credential: ImportedOfflineCredential,
  ): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const entityId = toEntityId(credentialStorageId);
    const wire: Record<string, unknown> = {
      ...credential,
      created_at:
        credential.created_at instanceof Date
          ? credential.created_at.toISOString()
          : String(credential.created_at),
      last_used_at:
        credential.last_used_at instanceof Date
          ? credential.last_used_at.toISOString()
          : String(credential.last_used_at),
      expires_at:
        credential.expires_at instanceof Date
          ? credential.expires_at.toISOString()
          : String(credential.expires_at),
      locked_until:
        credential.locked_until === undefined || credential.locked_until === null
          ? undefined
          : credential.locked_until instanceof Date
            ? credential.locked_until.toISOString()
            : String(credential.locked_until),
      sync_required: credential.sync_required,
    };

    const existing = await getStoreRow(STORE_CREDENTIALS, credentialClientId, entityId);
    const row = await buildRecordRow(STORE_CREDENTIALS, credentialClientId, entityId, wire, existing);
    await db.records.put(row);
  },

  async mergeCredentialPayload(
    credentialClientId: number,
    credentialStorageId: string,
    patch: Partial<ImportedOfflineCredential>,
  ): Promise<void> {
    const cur = await this.getCredentialPayload(credentialClientId, credentialStorageId);
    if (!cur) {
      return;
    }
    await this.upsertCredentialPayload(credentialClientId, credentialStorageId, { ...cur, ...patch });
  },

  /** Removes all offline-auth credential rows for one tenant (client switch isolation). */
  async deleteCredentialsForClient(clientId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    const rows = await db.records
      .where('[domain+store+client_id]')
      .equals([DOMAIN, STORE_CREDENTIALS, clientId])
      .toArray();
    const ids = rows.map((r) => r.id).filter((id): id is number => typeof id === 'number');
    if (ids.length > 0) {
      await db.records.bulkDelete(ids);
    }
  },

  async getCredentialPayload(credentialClientId: number, credentialStorageId: string): Promise<ImportedOfflineCredential | undefined> {
    const row = await getStoreRow(STORE_CREDENTIALS, credentialClientId, toEntityId(credentialStorageId));
    const raw = await decryptRecordPayload<Record<string, unknown>>(row);
    if (!raw || typeof raw !== 'object') {
      return undefined;
    }
    return raw as unknown as ImportedOfflineCredential;
  },

  async deleteCredential(credentialClientId: number, credentialStorageId: string): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const row = await getStoreRow(STORE_CREDENTIALS, credentialClientId, toEntityId(credentialStorageId));
    if (typeof row?.id === 'number') {
      await db.records.delete(row.id);
    }
  },

  async listCredentialPayloadsScanAllStores(): Promise<ImportedOfflineCredential[]> {
    const db = getDb();
    if (!db) {
      return [];
    }

    const rows = await db.records.filter((r) => r.domain === DOMAIN && r.store === STORE_CREDENTIALS).toArray();
    const out: ImportedOfflineCredential[] = [];
    for (const row of rows) {
      const value = await decryptRecordPayload<Record<string, unknown>>(row);
      if (value && typeof value === 'object') {
        out.push(value as unknown as ImportedOfflineCredential);
      }
    }
    return out;
  },

  /** Filter by lowercase email across all tenants (small N). */
  async filterCredentialsByEmail(lowerEmail: string): Promise<ImportedOfflineCredential[]> {
    const list = await this.listCredentialPayloadsScanAllStores();
    return list.filter((c) => String(c.email).toLowerCase() === lowerEmail);
  },

  async clearAllCredentialsAcrossClients(): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const rows = await db.records.filter((r) => r.domain === DOMAIN && r.store === STORE_CREDENTIALS).toArray();
    const ids = rows
      .filter((r) => r.domain === DOMAIN && r.store === STORE_CREDENTIALS)
      .map((r) => r.id)
      .filter((id): id is number => typeof id === 'number');
    if (ids.length > 0) {
      await db.records.bulkDelete(ids);
    }
  },

  /**
   * Clear offline credentials for a specific client only. Used when a
   * membership is terminated but the user has other active memberships —
   * only the terminated client's credentials are removed, preserving
   * access to other clients.
   *
   * Uses the [domain+store+client_id] compound index for efficient deletion.
   */
  async clearCredentialsForClient(clientId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const rows = await db.records
      .where('[domain+store+client_id]')
      .equals([DOMAIN, STORE_CREDENTIALS, clientId])
      .toArray();
    const ids = rows
      .map((r) => r.id)
      .filter((id): id is number => typeof id === 'number');
    if (ids.length > 0) {
      await db.records.bulkDelete(ids);
    }
  },

  async clearCredentialsMatchingEmail(lowerEmail: string): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const matches = await this.filterCredentialsByEmail(lowerEmail);
    for (const c of matches) {
      await this.deleteCredential(c.client_id, c.id);
    }
  },

  async upsertAuthConfig(config: ImportedOfflineAuthConfig): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const wire: Record<string, unknown> = {
      ...config,
      last_online_sync:
        config.last_online_sync instanceof Date ? config.last_online_sync.toISOString() : config.last_online_sync,
    };

    const existing = await getStoreRow(STORE_CONFIG, AUTH_CONFIG_CLIENT_ID, AUTH_CONFIG_ENTITY_ID);
    const row = await buildRecordRow(STORE_CONFIG, AUTH_CONFIG_CLIENT_ID, AUTH_CONFIG_ENTITY_ID, wire, existing);
    await db.records.put(row);
  },

  async getAuthConfigPayload(): Promise<ImportedOfflineAuthConfig | undefined> {
    const row = await getStoreRow(STORE_CONFIG, AUTH_CONFIG_CLIENT_ID, AUTH_CONFIG_ENTITY_ID);
    const raw = await decryptRecordPayload<Record<string, unknown>>(row);
    if (!raw || typeof raw !== 'object') {
      return undefined;
    }
    return raw as unknown as ImportedOfflineAuthConfig;
  },
};

/** One-time migrate from Dexie TotalAccessOfflineAuthDB into unified offline DB + delete legacy DB name. */
export async function migrateLegacyOfflineAuthDatabaseIfNeeded(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  legacyMigratePromise ??= (async () => {
    const guard = '__ta_offline_auth_migrated_to_unified_db_v1__';
    if (typeof window !== 'undefined' && window.localStorage.getItem(guard) === '1') {
      return;
    }

    const existsLegacy = await Dexie.exists('TotalAccessOfflineAuthDB');
    if (!existsLegacy) {
      window.localStorage.setItem(guard, '1');
      return;
    }

    let legacyDexie: Dexie | null = null;
    try {
      legacyDexie = new Dexie('TotalAccessOfflineAuthDB');
      legacyDexie.version(1).stores({
        credentials: 'id, email, client_id, user_id, expires_at, last_used_at',
        config: 'id',
      });
      await legacyDexie.open();

      const credRows = await legacyDexie.table('credentials').toArray();
      const cfgRow = await legacyDexie.table('config').get('config');

      for (const row of credRows) {
        const r = row as ImportedOfflineCredential;
        if (!r?.id) {
          continue;
        }

        await authOfflineStore.upsertCredentialPayload(
          Number(r.client_id) || 0,
          r.id,
          {
            ...r,
            email: typeof r.email === 'string' ? r.email.toLowerCase() : String(r.email),
          },
        );
      }

      if (cfgRow && typeof cfgRow === 'object' && cfgRow !== null && (cfgRow as { id?: string }).id === 'config') {
        await authOfflineStore.upsertAuthConfig(cfgRow as unknown as ImportedOfflineAuthConfig);
      }

      legacyDexie.close();
      legacyDexie = null;

      try {
        await Dexie.delete('TotalAccessOfflineAuthDB');
      } catch {
        /* ignore duplicate delete */
      }

      window.localStorage.setItem(guard, '1');
    } catch (err) {
      console.warn('[authOfflineStore] Legacy offline-auth migration skipped:', err);
    } finally {
      if (legacyDexie) {
        legacyDexie.close();
      }
    }
  })();

  return legacyMigratePromise;
}

export default authOfflineStore;
